import React from 'react';
import { STATUS_WORDS, STATUS_TONE, KIND_WORDS } from './api.js';

export function Pill({ children, tone = 'bg-slate-100 text-slate-700 ring-slate-200', className = '' }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${tone} ${className}`}>{children}</span>;
}
export function StatusPill({ status }) { return <Pill tone={STATUS_TONE[status] || STATUS_TONE.not_started}>{STATUS_WORDS[status] || status}</Pill>; }
export function KindPill({ kind }) { return <Pill tone="bg-white text-slate-600 ring-slate-200">{KIND_WORDS[kind] || kind}</Pill>; }

/** The item by CMS's own reference ("H.III.1") in the app's orange tint, never dark. */
export function ItemBadge({ cmsRef, seq, className = '' }) {
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-orange-50 text-orange-800 ring-1 ring-orange-200 whitespace-nowrap ${className}`} title={seq ? `Item ${seq} of the checklist, in CMS order` : undefined}>{cmsRef || `Item ${seq}`}</span>;
}

export function TabBar({ value, onChange, tabs }) {
  return (
    <div className="inline-flex rounded-xl bg-slate-100 p-1 gap-0.5 flex-wrap">
      {tabs.map(([v, l, count]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 ${value === v ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          {l}
          {typeof count === 'number' && count > 0 && (
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${value === v ? 'bg-slate-100 text-slate-700' : 'bg-slate-200/70 text-slate-500'}`}>{count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

export function Card({ title, children, right, className = '', tone = '' }) {
  return (
    <section className={`bg-white rounded-2xl border ${tone || 'border-slate-200'} ${className}`}>
      {(title || right) && (
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          {right}
        </div>
      )}
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

export function Button({ children, onClick, kind = 'primary', disabled, type = 'button', className = '', title }) {
  const base = 'px-4 py-2 rounded-xl text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed';
  const styles = {
    primary: 'bg-orange-600 text-white hover:bg-orange-700 shadow-sm',
    secondary: 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50',
    good: 'bg-emerald-700 text-white hover:bg-emerald-800 shadow-sm',
    danger: 'bg-white text-rose-700 border border-rose-200 hover:bg-rose-50',
    ghost: 'text-slate-600 hover:text-slate-900',
  };
  return <button type={type} onClick={onClick} disabled={disabled} title={title} className={`${base} ${styles[kind]} ${className}`}>{children}</button>;
}

export function Field({ label, children, hint, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
    </label>
  );
}
export const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-200';

export function Progress({ pct, tone = 'bg-emerald-600' }) {
  return <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-2 rounded-full ${tone}`} style={{ width: `${Math.max(0, Math.min(100, Number(pct) || 0))}%` }} /></div>;
}

export function Stat({ label, value, sub, tone = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-200 px-4 py-3 ${tone}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-0.5">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export function Empty({ children }) { return <div className="text-sm text-slate-500 py-6 text-center">{children}</div>; }

/** Section letter and short title, then the topic, in words. */
export function Where({ item, className = '' }) {
  return <span className={`text-xs text-slate-500 ${className}`}>{item.section_title} · {item.topic_title}</span>;
}
